import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
  ConditionalTemplateQuestionSet_CreateConditionalTemplateQuestionSetInput,
  ConditionalTemplateQuestionSet_FieldsFragment,
  ConditionalTemplateQuestionSet_GetAllConditionalTemplateQuestionSetsDocument,
  ConditionalTemplateQuestionSet_GetAllConditionalTemplateQuestionSetsForConditionalTemplateQuestionSetDataGridDocument,
  ConditionalTemplateQuestionSet_UpdateConditionalTemplateQuestionSetInput,
  useConditionalTemplateQuestionSet_CreateConditionalTemplateQuestionSetMutation,
  useConditionalTemplateQuestionSet_UpdateConditionalTemplateQuestionSetMutation,
} from 'queries';
import * as yup from 'yup';

import FormTextField from 'common/form-text-field';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
import { defaultPaginationModel } from 'utils/pagination-model';

type Props = {
  isEditing: boolean;
  selectedConditionalTemplateQuestionSet?:
    | ConditionalTemplateQuestionSet_FieldsFragment
    | undefined;
  onExitClick: () => void;
};

const useStyles = makeStyles(() => ({
  h5: {
    paddingTop: '5px',
  },
  formSection: {
    paddingBottom: '10px',
  },
  form: {
    textAlign: 'left',
    '& input': {
      paddingRight: '10px',
      padding: '1vh 2vh',
    },
  },
  inputGrid: {
    paddingRight: '20px',
  },
  inputError: {
    backgroundColor: '#FFF1F1',
    color: 'red',
  },
  container: {
    textAlign: 'left',
  },
  submitButton: {
    paddingTop: '10px',
  },
}));

type ConditionalTemplateQuestionSetFormValues = {
  name: string;
};

const requiredTextFields = ['name'];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const requiredTextFieldValidation = requiredTextFields.reduce((acc: any, val: string) => {
  acc[val] = yup.string().required('Required');
  return acc;
}, {});

const schema = yup.object().shape({
  ...requiredTextFieldValidation,
});

export default function ConditionalTemplateQuestionSetForm({
  isEditing,
  selectedConditionalTemplateQuestionSet,
  onExitClick,
}: Props) {
  const classes = useStyles();

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    control,
  } = useForm<ConditionalTemplateQuestionSetFormValues>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
    defaultValues: {
      name: selectedConditionalTemplateQuestionSet?.name || '',
    },
  });

  const [
    createConditionalTemplateQuestionSet,
    { loading: executingCreateConditionalTemplateQuestionSetMutation },
  ] = useConditionalTemplateQuestionSet_CreateConditionalTemplateQuestionSetMutation({
    refetchQueries: [
      {
        query:
          ConditionalTemplateQuestionSet_GetAllConditionalTemplateQuestionSetsForConditionalTemplateQuestionSetDataGridDocument,
        variables: {
          input: {
            paginationInput: {
              page: defaultPaginationModel.page,
              pageSize: defaultPaginationModel.pageSize,
            },
          },
        },
      },
    ],
    onCompleted: () => {
      showToast({
        message: 'Conditional template question set created.',
        severity: NotificationSeverity.SUCCESS,
      });
      onExitClick();
    },
    onError: (e) => {
      showAlert({
        message: 'Error creating conditional template question set: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [
    updateConditionalTemplateQuestionSet,
    { loading: executingUpdateConditionalTemplateQuestionSetMutation },
  ] = useConditionalTemplateQuestionSet_UpdateConditionalTemplateQuestionSetMutation({
    refetchQueries: [
      { query: ConditionalTemplateQuestionSet_GetAllConditionalTemplateQuestionSetsDocument },
    ],
    onCompleted: () => {
      showToast({
        message: 'Conditional template question set updated',
        severity: NotificationSeverity.SUCCESS,
      });
      onExitClick();
    },
    onError: (e) => {
      showAlert({
        message: 'Error updating conditional template question set: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const submitForm = async ({ ...formData }: ConditionalTemplateQuestionSetFormValues) => {
    if (selectedConditionalTemplateQuestionSet) {
      const input: ConditionalTemplateQuestionSet_UpdateConditionalTemplateQuestionSetInput = {
        ...formData,
        id: selectedConditionalTemplateQuestionSet.id,
      };

      updateConditionalTemplateQuestionSet({
        variables: {
          input,
        },
      });
    } else {
      const input: ConditionalTemplateQuestionSet_CreateConditionalTemplateQuestionSetInput = {
        ...formData,
      };

      createConditionalTemplateQuestionSet({
        variables: {
          input,
        },
      });
    }
  };

  return (
    <>
      <Typography variant="h5" className={classes.h5}>
        {isEditing ? 'Edit' : 'Create'} Conditional Template Question Set
      </Typography>
      <form
        id="conditional-template-question-set-form"
        className={classes.form}
        onSubmit={handleSubmit(submitForm)}
      >
        <div className={classes.formSection}>
          <Grid container className={classes.container}>
            <Grid className={classes.inputGrid} item xs={12} sm={12}>
              <Controller
                name="name"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <FormTextField
                    {...field}
                    inputRef={ref}
                    autoFocus
                    inputProps={{ maxLength: 300 }}
                    label="Set name"
                    errorMessage={errors.name?.message}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container className={clsx(classes.container, classes.submitButton)}>
            <Grid className={classes.inputGrid} item xs={12} sm={6}>
              <Button
                type="submit"
                form="conditional-template-question-set-form"
                color="secondary"
                variant="contained"
                disabled={
                  isSubmitting ||
                  executingCreateConditionalTemplateQuestionSetMutation ||
                  executingUpdateConditionalTemplateQuestionSetMutation
                }
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </div>
      </form>
    </>
  );
}
